// FILE: calendarGrid.ts
// Purpose: Month arithmetic for the in-app date picker, with no dependency on how the machine is
//          configured.
// Layer: Logic
// Exports: CalendarDay, buildMonthGrid, shiftMonth, formatMonthLabel, WEEKDAY_LABELS,
//          toDateInputValue, fromDateInputValue, HOUR_OPTIONS, MINUTE_OPTIONS

/**
 * A day cell in the rendered month.
 *
 * `value` is the `YYYY-MM-DD` form the rest of the snooze code already speaks,
 * so the picker is a drop-in for the browser's own date field and nothing
 * downstream has to change.
 */
export interface CalendarDay {
  readonly value: string;
  readonly dayOfMonth: number;
  /** False for the leading and trailing days borrowed from the neighbouring months. */
  readonly inCurrentMonth: boolean;
}

/**
 * Weekdays as this app writes them, Monday first.
 *
 * Hard-coded rather than derived from `Intl`: the whole point of this picker is
 * that two people looking at the same thread see the same calendar, and the
 * first day of the week is exactly the kind of thing that differs between an
 * `es-ES` machine and an `en-US` one.
 */
export const WEEKDAY_LABELS = ["L", "M", "X", "J", "V", "S", "D"] as const;

/** Local-time `YYYY-MM-DD`, never `toISOString` — that one shifts the day near midnight. */
export function toDateInputValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

/**
 * Read a `YYYY-MM-DD` back as a local date at midnight.
 *
 * Built from parts for the same reason `resolveCustomSnooze` is: passing the
 * bare string to `new Date` is read as UTC, which lands on the previous day for
 * anyone west of Greenwich.
 */
export function fromDateInputValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  // Rejects 31 de febrero, which the constructor would silently roll forward.
  if (date.getMonth() !== Number(month) - 1 || date.getDate() !== Number(day)) return null;
  return date;
}

/**
 * The six-week grid for the month `anchor` falls in.
 *
 * Always six rows, so the popup does not change height as somebody pages
 * through months — a calendar that grows and shrinks moves the buttons under
 * the cursor.
 */
export function buildMonthGrid(anchor: Date): ReadonlyArray<CalendarDay> {
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  // `getDay()` counts from Sunday; this shifts it to a Monday-first week.
  const leading = (firstOfMonth.getDay() + 6) % 7;
  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - leading);

  const days: CalendarDay[] = [];
  for (let index = 0; index < 42; index += 1) {
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
    days.push({
      value: toDateInputValue(day),
      dayOfMonth: day.getDate(),
      inCurrentMonth: day.getMonth() === anchor.getMonth(),
    });
  }
  return days;
}

/**
 * The same day-of-month one or more months away, clamped to the month's length.
 *
 * Clamped because the naive `setMonth` overflows: 31 de enero + 1 mes lands on
 * el 2 o el 3 de marzo, and paging a calendar would skip February entirely.
 */
export function shiftMonth(anchor: Date, delta: number): Date {
  const target = new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1);
  const lastDayOfTarget = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(anchor.getDate(), lastDayOfTarget));
  return target;
}

/** "marzo de 2026", capitalised — the heading above the grid. */
export function formatMonthLabel(anchor: Date): string {
  const label = anchor.toLocaleDateString("es", { month: "long", year: "numeric" });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** 00–23. A fixed 24-hour list, so no reader gets an AM/PM control and another a 24h one. */
export const HOUR_OPTIONS: ReadonlyArray<string> = Array.from({ length: 24 }, (_, hour) =>
  String(hour).padStart(2, "0"),
);

/**
 * Minutes in steps of five.
 *
 * A snooze is a rough intention — "después de comer", "mañana temprano" — and
 * the sweep resolves it to within half a minute anyway, so minute-level
 * precision would be a longer list pretending to an accuracy that is not there.
 */
export const MINUTE_OPTIONS: ReadonlyArray<string> = Array.from({ length: 12 }, (_, step) =>
  String(step * 5).padStart(2, "0"),
);
